import { cn } from "@/frontend/lib/utils";
import type { AvailableRoom } from "@/shared/types";

interface AvailableRoomsListProps {
  rooms: AvailableRoom[];
  selectedRoomTypeId?: string;
  selectedOccupants?: number;
  onSelect: (room: AvailableRoom) => void;
  isLoading: boolean;
}

interface ResidenceGroup {
  residenceId: string;
  residenceName: string;
  rooms: AvailableRoom[];
}

const SkeletonCard = () => (
  <div className="rounded-xl border border-border overflow-hidden animate-pulse">
    <div className="aspect-[4/3] bg-muted" />
    <div className="p-4 space-y-2">
      <div className="h-4 bg-muted rounded w-3/4" />
      <div className="h-3 bg-muted rounded w-1/2" />
      <div className="h-4 bg-muted rounded w-1/3" />
    </div>
  </div>
);

export const AvailableRoomsList = ({
  rooms,
  selectedRoomTypeId,
  selectedOccupants,
  onSelect,
  isLoading,
}: AvailableRoomsListProps) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (rooms.length === 0) {
    return (
      <div className="text-center py-10 px-4 rounded-xl border border-border bg-muted/30">
        <p className="text-muted-foreground font-medium">
          No rooms available for this date.
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          Try selecting a different move-in date.
        </p>
      </div>
    );
  }

  const selectedRoom = selectedRoomTypeId
    ? rooms.find((r) => r.roomTypeId === selectedRoomTypeId)
    : null;

  // Once a room is selected, collapse the grid to just that card
  const visibleRooms = selectedRoom ? [selectedRoom] : rooms;

  // Group rooms by residence, preserving backend order (which is sorted by
  // cheapest available room — so the cheapest residence comes first).
  const residenceGroupMap = new Map<string, ResidenceGroup>();
  for (const room of visibleRooms) {
    const group = residenceGroupMap.get(room.residenceId);
    if (group) {
      group.rooms.push(room);
    } else {
      residenceGroupMap.set(room.residenceId, {
        residenceId: room.residenceId,
        residenceName: room.residenceName,
        rooms: [room],
      });
    }
  }
  const residenceGroups = Array.from(residenceGroupMap.values());

  return (
    <div className="space-y-6">
      {residenceGroups.map((group) => (
        <section key={group.residenceId} className="space-y-3">
          <h3 className="font-heading text-lg font-semibold text-foreground">
            Available Rooms – {group.residenceName}
          </h3>
          <div
            className={cn(
              "grid gap-4",
              selectedRoom
                ? "grid-cols-1 sm:grid-cols-2"
                : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
            )}
          >
            {group.rooms.map((room) => {
              const isSelected = room.roomTypeId === selectedRoomTypeId;
              const minPrice = Math.min(...room.priceByOccupancy);
              const exactPrice = selectedOccupants
                ? room.priceByOccupancy[selectedOccupants - 1]
                : undefined;

              return (
                <button
                  key={room.roomTypeId}
                  type="button"
                  onClick={() => onSelect(room)}
                  className={cn(
                    "text-left rounded-xl border overflow-hidden transition-all duration-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary",
                    isSelected
                      ? "border-secondary ring-2 ring-secondary shadow-md"
                      : "border-border hover:border-secondary/50"
                  )}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                    {room.thumbnailUrl ? (
                      <img
                        src={room.thumbnailUrl}
                        alt={room.roomTypeName}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <span className="text-sm">No image</span>
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute inset-0 bg-secondary/10" />
                    )}
                  </div>
                  <div className="p-4">
                    <p className="font-heading font-semibold text-foreground text-sm leading-snug">
                      {room.roomTypeName}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {room.residenceName}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-secondary">
                      {exactPrice != null ? (
                        <>€{exactPrice}<span className="text-xs font-normal text-muted-foreground">/month</span></>
                      ) : (
                        <>
                          {room.priceByOccupancy.length > 1 ? "from " : ""}
                          €{minPrice}
                          <span className="text-xs font-normal text-muted-foreground">/month</span>
                        </>
                      )}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
      {selectedRoom && (
        <button
          type="button"
          onClick={() => onSelect(selectedRoom)}
          className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
        >
          Change room ({rooms.length} available)
        </button>
      )}
    </div>
  );
};
